import React from 'react';
import PropTypes from 'prop-types';
import { url } from 'hpstore-helpers';

import Link from './hyperlink';
import { Helpers } from '../../core/src/helpers';
import withWindowResize from '../../ui/components/window-resize';
import JsonLd from './json-ld';
import { fetchBreadcrumbs } from '../actions';
import { MetricsElement } from 'react-metrics';

import '../css/bread-crumb.less';

const DEFAULT_BREADCRUMB = [
    { href: process.env.BASENAME, text: 'Store Home' }
];

class Breadcrumb extends React.PureComponent {
    static DEFAULT_BREADCRUMB = DEFAULT_BREADCRUMB;

    static propTypes = {
        /** ID of the product */
        productId: PropTypes.string,
        /** Model id of the product */
        modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** Array of custom breadcrumbs, an alternative to passing productId and modelId */
        customBreadcrumbs: PropTypes.array,
        /** Bool to control unlinking of the last breadcrumb */
        unlinkTrailing: PropTypes.bool
    }

    static defaultProps = {
        productId: '',
        modelId: '',
        unlinkTrailing: false,
        customBreadcrumbs: []
    }

    state = {
        loaded: false,
        productId: null,
        modelId: null,
        breadcrumbs: []
    }
    _isMounted = false;

    constructor(props) {
        super(props);
        const { customBreadcrumbs } = props;
        //temp fix for SSR
        if (customBreadcrumbs.length) {
            this.state.breadcrumbs = this.buildCustomBreadcrumbs(customBreadcrumbs, true);
        }
    }

    componentDidMount() {
        const { productId, modelId } = this.props;
        this._isMounted = true;
        this.getBreadcrumbs(productId, modelId);
    }

    componentDidUpdate(prevProps) {
        const { productId, modelId } = this.props;
        const { productId: oldProductId, modelId: oldModelId } = prevProps;
        const isNewProduct = productId && (productId !== oldProductId || modelId !== oldModelId);
        if (!!isNewProduct || this.props.customBreadcrumbs !== prevProps.customBreadcrumbs) {
            this.getBreadcrumbs(productId, modelId);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    getBreadcrumbs = (productId, modelId) => {
        const { customBreadcrumbs } = this.props;
        if (customBreadcrumbs.length) {
            this.updateBreadcrumbs(
                this.buildCustomBreadcrumbs(customBreadcrumbs, true)
            );
        } else {
            if (!productId && !modelId) {
                this.updateBreadcrumbs(
                    this.buildCustomBreadcrumbs(DEFAULT_BREADCRUMB)
                );
            } else {
                fetchBreadcrumbs(productId, modelId)
                    .then(breadcrumbs => {
                        if (breadcrumbs) {
                            this.updateBreadcrumbs(breadcrumbs);
                        }
                    })
                    .catch(() => this.setState({ loaded: true }));
            }
        }
    }



    buildCustomBreadcrumbs = (customBreadcrumbs, useTemplate) => {
        const { unlinkTrailing, enableClickTracking } = this.props;
        let templateBreadcrumbs = useTemplate
            ? this.generateTemplateBreadcrumbs()
            : customBreadcrumbs;

        const metrics = this.getMetricsData();

        return templateBreadcrumbs.reduce((result, item, index) => {
            const isTrailingItem = index === templateBreadcrumbs.length - 1;
            const itemLink = item.link || item.href;
            const itemName = item.name || item.text;
            const builtBreadcrumb = unlinkTrailing && isTrailingItem
                ? <span className="no-select" {...metrics}>{itemName}</span>
                : <Link id={`WC_BreadCrumb_Link_${index + 1}`} to={itemLink} {...metrics}>{itemName}</Link>;

            let finalBreadcrumb = enableClickTracking
                ? <MetricsElement>{builtBreadcrumb}</MetricsElement>
                : builtBreadcrumb;
            result.push(finalBreadcrumb);
            if (index < templateBreadcrumbs.length - 1) {
                result.push(<span className="no-select"> / </span>);
            }

            return result;
        }, []);
    }

    updateBreadcrumbs = breadcrumbs => {
        if (this._isMounted) {
            this.setState({ breadcrumbs, loaded: true });
        }
    }

    getJsonLdBreadcrumbData = (breadcrumbs) => {
        const itemListElement = breadcrumbs.reduce((jsonLdArray, breadcrumbItem, i) => {
            let { name, href, link, text } = breadcrumbItem || {};
            const breadcrumbLink = link || href;
            if (!breadcrumbLink || breadcrumbLink.length === 0) {
                return jsonLdArray;
            }
            let itemHref = (breadcrumbLink[0] === '/')
                ? Helpers.getAbsoluteRedirectPath(breadcrumbLink)
                : breadcrumbLink;
            jsonLdArray.push({
                "@type": "ListItem",
                "position": i + 1,
                name: name || text,
                "item": itemHref
            })

            return jsonLdArray;
        }, []);

        return itemListElement && itemListElement.length > 0 ? ({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement
        }) : null;
    }

    getJsonLdBreadcrumbs = ({ breadcrumbs }) => {
        let parsedBreadcrumbs = breadcrumbs.slice();
        const jsonLdData = this.getJsonLdBreadcrumbData(parsedBreadcrumbs);
        return jsonLdData ? <JsonLd type="breadcrumb" data={jsonLdData} /> : null;
    }

    getMetricsData = () => this.props.enableClickTracking
        ? ({
            'data-metrics-event-name': "linkClick",
            'data-metrics-event': "e_linkClick",
            'data-metrics-link-placement': 'breadcrumb',
            'data-metrics-link-id': 'click'
        })
        : {};

    generateTemplateBreadcrumbs = () => {
        const { templateKey, match, customBreadcrumbs, title, product } = this.props || {};

        switch (templateKey) {
            case 'dlp':
            case 'dlp-v2':
                let { url } = match || {};
                return [
                    Breadcrumb.DEFAULT_BREADCRUMB[0],
                    ...customBreadcrumbs && customBreadcrumbs.length > 0 ? customBreadcrumbs : Breadcrumb.DEFAULT_BREADCRUMB,
                    ...title && typeof url === 'string'
                        ? [{ href: url, text: title }]
                        : []
                ];

            case 'pdp':
                let { name } = product || {};
                let templateBreadcrumbs = [];
                try {
                    templateBreadcrumbs = customBreadcrumbs.slice();
                    if (templateBreadcrumbs.length > 0 && templateBreadcrumbs.push) {
                        templateBreadcrumbs.push({ name });
                    }
                } catch (e) { }

                return templateBreadcrumbs;
            default:
                return customBreadcrumbs;
        }
    }

    getMobileBreadcrumbs = (templateBreadcrumbs) => {
        let item;
        let breadcrumb;
        let metrics = this.getMetricsData();
        if (templateBreadcrumbs.length >= 2) {
            item = templateBreadcrumbs[templateBreadcrumbs.length - 2];
            breadcrumb = <Link className='breadcrumb-item mobile' to={item.link || item.href} {...metrics}>Back to {item.name || item.text}</Link>
        } else if (templateBreadcrumbs.length > 0) {
            item = templateBreadcrumbs[templateBreadcrumbs.length - 1];
            breadcrumb = <span className='breadcrumb-item mobile no-select' {...metrics}>{item.name || item.text}</span>
        }

        return breadcrumb
            ? this.props.enableClickTracking
                ? <MetricsElement>{breadcrumb}</MetricsElement>
                : breadcrumb
            : null;
    }

    render() {
        const { templateKey, customBreadcrumbs, showJsonLd, className, device, disableMobileBreadcrumb, isV2: isV2prop, onBreadcrumbClick } = this.props,
            { breadcrumbs: stateBreadcrumbs } = this.state;

        let templateBreadcrumbs = this.generateTemplateBreadcrumbs();
        const isV2 = templateKey in { 'filters-v2': 1, 'sale-v4': 1, 'compare-v2': 1, 'dlp-v2': 1 } || isV2prop;

        return (
            <div className={`breadcrumb ${className ? ' ' + className : ''}`}>
                { (disableMobileBreadcrumb || device !== 'mobile' || !isV2) && stateBreadcrumbs.map((breadcrumb, idx) => (
                    <div className={`breadcrumb-item${idx === stateBreadcrumbs.length - 1 ? ' last-breadcrumb' : ''}`} key={idx}>
                        {typeof breadcrumb === 'string'
                            ? <div dangerouslySetInnerHTML={Helpers.createMarkup(breadcrumb)} />
                            : breadcrumb}
                    </div>
                ))}
                { isV2 && !disableMobileBreadcrumb && device === 'mobile' && this.getMobileBreadcrumbs(templateBreadcrumbs)}
                {
                    showJsonLd && templateBreadcrumbs && templateBreadcrumbs.length > 0 && (
                        this.getJsonLdBreadcrumbs({
                            breadcrumbs: templateBreadcrumbs
                        })
                    )
                }
            </div>
        );
    }
}

export default withWindowResize(Breadcrumb);
